import type { RoomBookingState, ReservationItemSplitMode, RoomSplitInfo, RoomBooking, RoomBookingDate, RoomBlockDate, RoomBlockDateRoom } from "./types";
import { AvailabilityCheckResult, ReservationPerson } from './types';
import { getIslandItems, type ChainableItem } from "./model.SplitModeUtils";

import { modelState, ModelStateList } from "@/utils/modelState";
import { dateEqualityComparer } from "@/utils/utils";
import { v4 as uuidv4 } from 'uuid';
import { RatesRatePlanOverrides } from "./Rates/CustomRatePlan";
import { RatesBaseRateOverrides } from "./Rates/CustomBaseRate";
import { cloneReservationPerson, serializeReservationPerson, ReservationExistingPerson } from './model.ReservationPerson';
import { getAmountBeforeDiscountGross, getAmountAfterDiscountGross, calculateRowPrice, calculateDatePrice } from './model.Reservations';
import { sum } from 'lodash';
import { startOfDay } from 'date-fns';

export type { RoomSplitInfo };

// Adapter to make RoomBookingState compatible with ChainableItem
class RoomChainableAdapter implements ChainableItem {
    constructor(private room: RoomBookingState) {}
    
    get id(): string {
        return this.room.ReservationRoomId;
    }
    
    get splitFromId(): string | null {
        return this.room.SplitFromReservationRoomId;
    }
    
    get splitMode(): ReservationItemSplitMode {
        return this.room.SplitMode;
    }
    
    get startDate(): Date {
        return this.room.CheckIn.value;
    }
    
    get endDate(): Date {
        return this.room.CheckOut.value;
    }
}

// Room-specific utility functions

/**
 * Gets all rooms in the same island (connected component) as the given room,
 * sorted by check-in date.
 */
export function getRoomChain(roomId: string, allRooms: RoomBookingState[]): RoomBookingState[] {
    const adapters = allRooms.map(room => new RoomChainableAdapter(room));
    const adapterChain = getIslandItems(roomId, adapters);
    
    // Convert back to RoomBookingState objects
    return adapterChain.map(adapter =>
        allRooms.find(room => room.ReservationRoomId === adapter.id)!
    );
}

export function getRoomChainIndex(room: RoomBookingState, allRooms: RoomBookingState[]): number {
    const chain = getRoomChain(room.ReservationRoomId, allRooms);
    return chain.findIndex(r => r.ReservationRoomId === room.ReservationRoomId);
}

export function getNextRoomInChain(room: RoomBookingState, allRooms: RoomBookingState[]): RoomBookingState | null {
    return allRooms.find(r => r.SplitFromReservationRoomId === room.ReservationRoomId) || null;
}

export function getPreviousRoomInChain(room: RoomBookingState, allRooms: RoomBookingState[]): RoomBookingState | null {
    if (!room.SplitFromReservationRoomId) return null;
    return allRooms.find(r => r.ReservationRoomId === room.SplitFromReservationRoomId) || null;
}

/**
 * Deep-clones a single per-night date row into fresh `modelState`s, so the clone shares NO mutable
 * state with the source date. Without this the clone's `Dates` entries would be the SAME objects as
 * the source's, and any per-date mutation on the clone — notably `clearOpenPrice`'s per-date null in
 * the Change Room Type preview — would bleed straight back onto the live reservation's per-night
 * open prices (the "previewing loses the open price" / "no warning on per-date overrides" bug).
 */
export function cloneRoomBookingDate(d: RoomBookingDate): RoomBookingDate {
    return {
        Date: new Date(d.Date),
        VoucherEffects: d.VoucherEffects,
        PriceBreakdown: d.PriceBreakdown,
        SegmentIndex: modelState(d.SegmentIndex.value),
        RatePlanOverride: modelState(d.RatePlanOverride.value),
        BaseRateOverride: modelState(d.BaseRateOverride.value),
        RecalculateStayPeriod: modelState(d.RecalculateStayPeriod.value),
        UseOriginalRatePlan: modelState(d.UseOriginalRatePlan.value),
        MinorAdjustment: modelState(d.MinorAdjustment.value),
        CalculatedPrice: d.CalculatedPrice,
        AppliedVouchers: modelState(d.AppliedVouchers.value),
        NoShow: modelState(d.NoShow.value),
        NoCharge: modelState(d.NoCharge.value),
        AdditionalCharges: modelState(d.AdditionalCharges.value),
        PriceSourceDate: modelState<Date | null>(d.PriceSourceDate?.value ?? null),
        EvaluationSessionJson: modelState<string | null>(d.EvaluationSessionJson?.value ?? null),
        OpenPrice: modelState<number | null>(d.OpenPrice.value),
        OpenPriceDescription: modelState<string | null>(d.OpenPriceDescription.value),
        IgnoreVouchers: modelState<boolean | null>(d.IgnoreVouchers.value)
    };
}

export function cloneRoomBookingState(roomBookingState: RoomBookingState) {
    const roomType = modelState(roomBookingState.RoomType.value);

    const result: RoomBookingState =
    {
        //DataLoader: createRoomBookingDataLoader(roomBookingState.CheckIn.value, roomBookingState.CheckOut.value, roomType),
        ReservationRoomId: uuidv4(),
        RoomType: roomType,
        RoomLocationId: modelState(roomBookingState.RoomLocationId.value),
        CheckIn: modelState(roomBookingState.CheckIn.value, dateEqualityComparer),
        CheckOut: modelState(roomBookingState.CheckOut.value, dateEqualityComparer),
        Adults: modelState(roomBookingState.Adults.value),
        Children: modelState(roomBookingState.Children.value),
        Infants: modelState(roomBookingState.Infants.value),
        IsDirty: modelState(true),
        Price: {
            price: modelState({
                baseAmount: 0,
                payableAmount: null
            }),
            isLoading: modelState(false)
        },
        CalculatedPrice: modelState({
            breakdowns: [],
            totalBaseAmount: 0,
            totalPayableAmount: 0
        }),
        Dates: roomBookingState.Dates.map(cloneRoomBookingDate),
        People: new ModelStateList(roomBookingState.People.toArray().map(p => cloneRoomBookingPerson(p))),
        SomePeopleDirty: modelState(false),
        PeopleVersion: modelState(0),
        NoShow: modelState(false),
        NoShowDirty: modelState(false),
        NoShowVersion: modelState(0),
        NoCharge: modelState(false),
        NoChargeDirty: modelState(false),
        NoChargeVersion: modelState(0),
        SplitFromReservationRoomId: null,
        SplitMode: null,
        DatesUIVersion: modelState(0),
        RatePlanOverride: modelState(roomBookingState.RatePlanOverride.value),
        BaseRateOverride: modelState(roomBookingState.BaseRateOverride.value),
        RecalculateStayPeriod: modelState(roomBookingState.RecalculateStayPeriod.value),
        UseOriginalRatePlan: modelState(roomBookingState.UseOriginalRatePlan.value),
        AppliedVouchers: modelState(roomBookingState.AppliedVouchers.value),
        RoomBlocks: modelState(roomBookingState.RoomBlocks.value),
        RoomMovements: roomBookingState.RoomMovements,
        LockRate: modelState(false),
        Recalculate: modelState(false),
        AvailabilityCheckResult: modelState<AvailabilityCheckResult>({
            IsAvailable: roomBookingState.AvailabilityCheckResult.value.IsAvailable,
            IsBlockedByTempReservation: roomBookingState.AvailabilityCheckResult.value.IsBlockedByTempReservation,
            BlockedByTempReservationNumbers: roomBookingState.AvailabilityCheckResult.value.BlockedByTempReservationNumbers
        }),
        AdditionalCharges: modelState(new Array(...roomBookingState.AdditionalCharges.value)),
        RoomFormResponseJson: modelState(roomBookingState.RoomFormResponseJson.value),
        RoomFormFieldsMissingMandatoryNames: modelState<string[]>([]),
        // Carry the row-level open-price override forward on clone (parity with cloneProgramBookingState).
        // Root fix for change-room-type partial-split, split-room, split-stay "keep price", add-breaks,
        // and break-cluster commit paths, which all clone the source row.
        OpenPrice: modelState<number | null>(roomBookingState.OpenPrice.value),
        OpenPriceDescription: modelState<string | null>(roomBookingState.OpenPriceDescription.value),
        IgnoreVouchers: modelState(roomBookingState.IgnoreVouchers.value),
        IsExisting: false,
        ForceUnlocked: modelState(false)
    };

    return result;
}

export function cloneRoomBookingPerson(person: RoomBookingPerson): RoomBookingPerson {
    return {
        ReservationRoomPersonId: uuidv4(),
        Person: modelState(cloneReservationPerson(person.Person.value)),
        CheckIn: modelState(person.CheckIn.value, dateEqualityComparer),
        CheckOut: modelState(person.CheckOut.value, dateEqualityComparer),
        IsDirty: modelState(true)
    };
}


export interface RoomBookingPerson {
    ReservationRoomPersonId: string;
    Person: modelState<ReservationPerson>;
    CheckIn: modelState<Date>;
    CheckOut: modelState<Date>;
    IsDirty: modelState<boolean>;
}

export function RoomBookingToRoomBookingState(rb: RoomBooking): RoomBookingState {
    return {
        ...rb,
        //DataLoader: createRoomBookingDataLoader(new Date(rb.CheckIn), new Date(rb.CheckOut), roomType),
        RoomType: modelState(rb.RoomType),
        CheckIn: modelState(new Date(rb.CheckIn), dateEqualityComparer),
        CheckOut: modelState(new Date(rb.CheckOut), dateEqualityComparer),
        RoomLocationId: modelState(rb.RoomLocationId),
        Adults: modelState(rb.Adults),
        Children: modelState(rb.Children),
        Infants: modelState(rb.Infants ?? 0),
        Price: {
            price: modelState({
                baseAmount: (sum(rb.Dates.map(d => getAmountBeforeDiscountGross(d))) / 100),
                payableAmount: (sum(rb.Dates.map(d => getAmountAfterDiscountGross(d))) / 100)
            }),
            isLoading: modelState(false)
        },
        CalculatedPrice: modelState(calculateRowPrice(
            rb.Dates.map(d => d.PriceBreakdown),
            (sum(rb.Dates.map(d => getAmountBeforeDiscountGross(d))) / 100),
            (sum(rb.Dates.map(d => getAmountAfterDiscountGross(d))) / 100),
            rb.NoCharge
        )),
        Dates: rb.Dates.map<RoomBookingDate>(d => {
            return {
                Date: new Date(d.Date),
                VoucherEffects: d.VoucherEffects,
                PriceBreakdown: d.PriceBreakdown,
                SegmentIndex: modelState(d.SegmentIndex),
                RatePlanOverride: modelState(RatesRatePlanOverrides.deserializeRatePlanOverride(d.RatePlanOverride)),
                BaseRateOverride: modelState(RatesBaseRateOverrides.deserializeBaseRateOverride(d.BaseRateOverride)),
                RecalculateStayPeriod: modelState(null),
                UseOriginalRatePlan: modelState(d.UseOriginalRatePlan),
                MinorAdjustment: modelState(d.MinorAdjustment),
                CalculatedPrice: calculateDatePrice(
                    d.PriceBreakdown,
                    (getAmountBeforeDiscountGross(d) / 100),
                    (getAmountAfterDiscountGross(d) / 100),
                    rb.NoCharge),
                AppliedVouchers: modelState(d.Vouchers),
                NoShow: modelState(d.NoShow),
                NoCharge: modelState(d.NoCharge),
                AdditionalCharges: modelState(d.AdditionalCharges),
                PriceSourceDate: modelState<Date | null>(null),
                EvaluationSessionJson: modelState<string | null>(null),
                OpenPrice: modelState<number | null>(d.OpenPrice ?? null),
                OpenPriceDescription: modelState<string | null>(d.OpenPriceDescription ?? null),
                IgnoreVouchers: modelState<boolean | null>(d.IgnoreVouchers ?? null)
            };
        }),
        People: new ModelStateList(rb.People.map<RoomBookingPerson>(p => ({
            ReservationRoomPersonId: p.ReservationRoomPersonId,
            Person: modelState<ReservationExistingPerson>({
                [ReservationPerson]: true,
                [ReservationExistingPerson]: true,
                PersonId: p.PersonId,
                ContactId: null
            }),
            CheckIn: modelState(new Date(p.CheckIn), dateEqualityComparer),
            CheckOut: modelState(new Date(p.CheckOut), dateEqualityComparer),
            IsDirty: modelState(false)
        }))),
        SomePeopleDirty: modelState(false),
        PeopleVersion: modelState(0),
        NoShow: modelState(rb.NoShow),
        NoShowDirty: modelState(false),
        NoShowVersion: modelState(0),
        NoCharge: modelState(rb.NoCharge),
        NoChargeDirty: modelState(false),
        NoChargeVersion: modelState(0),
        IsDirty: modelState(false),
        SplitFromReservationRoomId: rb.SplitFromReservationRoomId,
        SplitMode: rb.SplitMode,
        DatesUIVersion: modelState(0),
        RatePlanOverride: modelState(RatesRatePlanOverrides.deserializeRatePlanOverride(rb.RatePlanOverride)),
        BaseRateOverride: modelState(RatesBaseRateOverrides.deserializeBaseRateOverride(rb.BaseRateOverride)),
        RecalculateStayPeriod: modelState(null),
        UseOriginalRatePlan: modelState(rb.UseOriginalRatePlan),
        AppliedVouchers: modelState(rb.Vouchers),
        RoomBlocks: modelState(rb.RoomBlocks.map<RoomBlockDate>(rbd => ({
            Date: startOfDay(new Date(rbd.Date)),
            Rooms: new ModelStateList<RoomBlockDateRoom>(rbd.Rooms.map(r => ({
                LocationId: r.LocationId,
                IsAllDay: r.IsAllDay,
                Start: new Date(r.Start),
                End: new Date(r.End),
                ReservationRoomId: r.ReservationRoomId
            }))),
            Version: modelState(0)
        }))),
        RoomMovements: modelState(rb.RoomMovements),
        LockRate: modelState(false),
        Recalculate: modelState(false),
        AvailabilityCheckResult: modelState({
            IsAvailable: rb.AvailabilityCheckResult.isAvailable,
            IsBlockedByTempReservation: rb.AvailabilityCheckResult.isBlockedByTempReservation,
            BlockedByTempReservationNumbers: rb.AvailabilityCheckResult.blockedByTempReservationNumbers
        }),
        AdditionalCharges: modelState(rb.AdditionalCharges),
        RoomFormResponseJson: modelState(rb.RoomFormResponseJson),
        RoomFormFieldsMissingMandatoryNames: modelState<string[]>([]),
        OpenPrice: modelState<number | null>(rb.OpenPrice ?? null),
        OpenPriceDescription: modelState<string | null>(rb.OpenPriceDescription ?? null),
        IgnoreVouchers: modelState(rb.IgnoreVouchers ?? false),
        IsExisting: true,
        ForceUnlocked: modelState(false)
    };
}